import type { HeadlineEntry } from "../store/useGame";
import type { DerivedAggregates, WorldState } from "../engine/types";
import { INDICATORS, trendFor } from "../engine/indicators";
import { useGame } from "../store/useGame";

interface Props {
  headlines: HeadlineEntry[];
  state: WorldState;
  aggregates: DerivedAggregates;
}

/**
 * Europe S2 — détecte si la headline provient d'une institution européenne
 * pour appliquer un stylage distinctif (fond bleu UE subtil, drapeau).
 */
function isEuropeanSource(source: string | undefined): boolean {
  if (!source) return false;
  return (
    source.includes("Commission européenne") ||
    source.includes("Conseil de l'Union") ||
    source.includes("Parlement européen") ||
    source.includes("MES ·") ||
    source.includes("Cour de justice de l'UE")
  );
}

function trendGlyph(direction: "up" | "down" | "flat"): string {
  if (direction === "up") return "↑";
  if (direction === "down") return "↓";
  return "→";
}

type TickerEntry =
  { kind: "news"; news: HeadlineEntry } | { kind: "indicator"; text: string };

/** Durée d'animation par item (s) — pace lente et constante quel que soit le
 *  nombre d'items affichés. */
const SECONDS_PER_ITEM = 8;

/**
 * V3 — bandeau de presse stylé par urgence, avec mentions de cotations des
 * indicateurs intercalées entre les news (« Label : valeur ↗ »).
 */
/** Le feed persisté monte à 60 entrées (analyse) ; le bandeau n'affiche
 *  que les plus récentes pour garder une boucle courte et lisible. */
const TICKER_WINDOW = 16;

export function PressTicker({ headlines: allHeadlines, state, aggregates }: Props) {
  const headlines = allHeadlines.slice(0, TICKER_WINDOW);
  const history = useGame((s) => s.indicatorHistory);

  const indicators = INDICATORS.filter(
    (def) => state.turn >= (def.revealedFromTurn ?? 0),
  ).map((def) => {
    const value = def.extract(state, aggregates);
    const direction = trendFor(history[def.id] ?? []).direction;
    return `${def.label} : ${def.format(value)}${def.unit} ${trendGlyph(direction)}`;
  });

  // Intercale une cotation entre chaque news.
  const entries: TickerEntry[] = [];
  const max = Math.max(headlines.length, indicators.length);
  for (let i = 0; i < max; i++) {
    if (i < headlines.length)
      entries.push({ kind: "news", news: headlines[i] });
    if (i < indicators.length)
      entries.push({ kind: "indicator", text: indicators[i] });
  }

  // Fix 2026-07-15 : si une copie est plus courte que le viewport (début de
  // partie, peu de headlines), la fin du cycle translateX(-50%) laissait la
  // moitié droite du bandeau VIDE (« le texte disparaît à moitié »). On
  // répète les entrées jusqu'à une taille plancher avant de doubler — la
  // boucle -50 % reste exacte (le contenu = 2 copies identiques).
  const MIN_ITEMS_PER_COPY = 16;
  const repeat = Math.max(
    1,
    Math.ceil(MIN_ITEMS_PER_COPY / Math.max(entries.length, 1)),
  );
  const copy = Array.from({ length: repeat }, () => entries).flat();
  // On duplique pour boucle ticker-scroll sans saut.
  const doubled = [...copy, ...copy];
  const durationSec = Math.max(60, Math.round(copy.length * SECONDS_PER_ITEM));

  const scrollToIndicators = () => {
    document
      .querySelector(".indicator-strip")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <footer
      className="press-ticker"
      aria-live="polite"
      role="button"
      tabIndex={0}
      title="Voir les indicateurs"
      onClick={scrollToIndicators}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          scrollToIndicators();
        }
      }}
    >
      <span className="ticker-track">
        {/* Fix 2026-07-15 (bis) : quand le contenu change (nouveau tour),
            la largeur de la bande ET animationDuration changent alors que
            l'animation CSS garde son temps écoulé → translateX(-X%) saute
            à une position arbitraire (texte coupé en début, voire raccord
            vide). La `key` remonte le rail à chaque changement de contenu :
            l'animation repart de zéro, bande pleine, news fraîches en tête.
            Entre deux tours, rien ne change → défilement stable. */}
        <span
          key={`${state.turn}-${entries.length}`}
          className="ticker-track-inner"
          style={{ animationDuration: `${durationSec}s` }}
        >
          {doubled.map((e, i) =>
            e.kind === "news" ? (
              <span
                key={i}
                className="ticker-item"
                data-urgency={e.news.urgency}
                data-eu={isEuropeanSource(e.news.source) ? "true" : undefined}
              >
                <span className="ticker-sep">·</span>
                {e.news.source && (
                  <span className="ticker-source">{e.news.source}</span>
                )}
                <span className="ticker-text">{e.news.text}</span>
              </span>
            ) : (
              <span key={i} className="ticker-item ticker-item-indicator">
                <span className="ticker-sep">·</span>
                <span className="ticker-indicator">{e.text}</span>
              </span>
            ),
          )}
        </span>
      </span>
    </footer>
  );
}
